import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, FileDoc, ArrowRight, Funnel, LockKey, CheckCircle, ArrowsLeftRight, MagnifyingGlass, BookOpen } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatDate } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import CopyCodeButton from "../components/CopyCodeButton";
import StatusBadge from "../components/StatusBadge";
import RoleGuard from "../components/RoleGuard";
import SearchableSelect from "../components/SearchableSelect";
import CurrencySelect from "../components/CurrencySelect";
import OwnershipToggle from "../components/OwnershipToggle";
import OwnerCell from "../components/OwnerCell";
import ReassignOwnerDialog from "../components/ReassignOwnerDialog";
import {
    ShowArchivedToggle, ArchivedBadge, ArchiveRowButton, UnarchiveRowButton, useArchiveAction,
} from "../components/ArchiveControls";
import FormDialog, { FormDialogCancel } from "../components/FormDialog";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const STATUS_OPTIONS = ["Draft", "Sent", "Approved", "Converted", "Rejected"];
// Iter 51.7 — module codes are now operator-managed (fetched from
// `/billing/proposal-modules/list` at dialog open time). The constant
// below is kept ONLY as the new-proposal default value when the API
// hasn't responded yet and as a stable smart-default identifier so
// the auto-prefill from a client's last approved proposal continues
// to override "the default" but not "an explicit operator choice".
const DEFAULT_MODULE_CODE = "MFBO";
const STATUS_BADGE_MAP = {
    Draft: { status: "Draft" },
    Sent: { status: "Open", className: "!bg-blue-50 !text-blue-700 !border-blue-200" },
    Approved: { status: "Locked", className: "!bg-emerald-50 !text-emerald-800 !border-emerald-200" },
    Converted: { status: "Active" },
    Rejected: { status: "Unpaid" },
};

function NewProposalDialog({ clients, templates, onCreated, open, onOpenChange, focusTemplate, prefillClientId }) {
    const [submitting, setSubmitting] = useState(false);
    const blank = { client_id: "", template_id: "", proposal_number: "", remarks: "", module_code: DEFAULT_MODULE_CODE, kind_attention: "", currency: "INR" };
    const [form, setForm] = useState(blank);
    // "Defaults from REE/.../#### (auto)" indicator
    const [defaults, setDefaults] = useState(null);
    const [loadingDefaults, setLoadingDefaults] = useState(false);
    // Iter 51.7 — operator-managed module codes loaded from the API.
    const [moduleCodes, setModuleCodes] = useState([]);
    const baselineRef = useRef(blank);

    // Iter 51.7 — fetch the active module codes whenever the dialog
    // opens (cheap query — handful of rows, server-side sorted). Stays
    // in sync with `/admin/module-codes` without a global refresh.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/billing/proposal-modules/list?include_inactive=false");
                if (!cancelled) setModuleCodes(data.items || []);
            } catch {
                // Silent — keep dropdown empty so the operator can still
                // type a proposal number manually if the API is offline.
                if (!cancelled) setModuleCodes([]);
            }
        })();
        return () => { cancelled = true; };
    }, [open]);

    // Reset form whenever the dialog opens
    useEffect(() => {
        if (open) {
            setForm(blank);
            baselineRef.current = blank;
            setDefaults(null);
            // PB-002 · Option C — When the dialog is opened via the "Create
            // New Proposal for this Client" affordance on an Approved proposal
            // detail page, prefill the client selection so /defaults fires
            // automatically for the operator.
            if (prefillClientId) {
                onClientChange(prefillClientId);
            }
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-applied defaults shouldn't trip dirty detection — we re-baseline
    // after smart defaults are pulled so the rep can still close the dialog
    // by clicking outside if they haven't actually typed anything yet.
    const isDirty = useMemo(
        () => JSON.stringify(form) !== JSON.stringify(baselineRef.current),
        [form],
    );

    // when template chosen, prefill module_code from template
    const onTemplateChange = (template_id) => {
        const tpl = templates.find((t) => t.id === template_id);
        setForm((f) => ({
            ...f,
            template_id,
            module_code: tpl?.module_code || f.module_code,
        }));
    };

    // when client chosen, fetch smart defaults from their last approved proposal
    const onClientChange = async (client_id) => {
        setForm((f) => ({ ...f, client_id }));
        setDefaults(null);
        if (!client_id) return;
        setLoadingDefaults(true);
        try {
            const { data } = await api.get(`/billing/proposal/defaults?client_id=${client_id}`);
            if (data?.from_proposal) {
                setDefaults(data);
                setForm((f) => {
                    const next = {
                        ...f,
                        kind_attention: f.kind_attention || data.kind_attention || "",
                        module_code: f.module_code === DEFAULT_MODULE_CODE && data.module_code ? data.module_code : f.module_code,
                    };
                    // Re-baseline so auto-applied defaults don't count as "dirty"
                    baselineRef.current = next;
                    return next;
                });
            } else {
                // no defaults found — re-baseline current form (only client_id was added)
                baselineRef.current = { ...baselineRef.current, client_id };
            }
        } catch {
            // silent — defaults are a nicety, never a blocker
        } finally {
            setLoadingDefaults(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.client_id) { toast.error("Pick a client"); return; }
        setSubmitting(true);
        try {
            const payload = { ...form };
            if (!form.template_id) delete payload.template_id;
            // Iter 66.12 — do not send module_code from the dialog. Backend
            // auto-derives from the chosen template (or defaults to "Custom"),
            // matching the simplified UI that no longer surfaces this field.
            delete payload.module_code;
            const { data } = await api.post("/billing/proposal/create", payload);
            toast.success(`Proposal ${data.proposal_number} created`);
            // Reset baseline so the FormDialog guard lets us close cleanly
            baselineRef.current = form;
            onOpenChange(false);
            onCreated?.(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to create proposal"));
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            isDirty={isDirty}
            contentClassName="max-w-md"
            testId="new-proposal-dialog"
            title={focusTemplate ? "New from Template" : "New proposal"}
            description={focusTemplate
                ? "Pick a template + client. The template's header text and line items will be hydrated automatically."
                : "Optionally hydrate from a template — header text + line items will be copied. You can edit afterwards."
            }
        >
                <form onSubmit={submit} className="space-y-3" data-testid="proposal-form">
                    <div>
                        <Label className="text-xs font-semibold">Client</Label>
                        <SearchableSelect
                            options={clients.map((c) => ({ value: c.id, label: c.name, hint: c.gstin || undefined }))}
                            value={form.client_id}
                            onChange={onClientChange}
                            placeholder="— Select client —"
                            searchPlaceholder="Type to filter clients…"
                            emptyText="No matching client."
                            testId="proposal-client-select"
                            className="mt-1.5"
                        />
                        {/* Smart-defaults pill (Iter 25) — surfaces the source proposal so reps know where the auto-fill came from */}
                        {loadingDefaults && (
                            <div className="mt-1.5 text-[10px] text-slate-500" data-testid="defaults-loading">Looking up defaults…</div>
                        )}
                        {defaults?.from_proposal && (
                            <div
                                className="mt-1.5 inline-flex items-center gap-1.5 rounded px-2 py-1 bg-amber-50 border border-amber-200 text-[10px] text-amber-800"
                                data-testid="defaults-source-pill"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                <span>Auto-defaults from <strong className="font-mono">{defaults.from_proposal.proposal_number}</strong></span>
                            </div>
                        )}
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Template (optional)</Label>
                        <SearchableSelect
                            options={[
                                { value: "", label: "— None — start blank —" },
                                ...templates.filter((t) => t.is_active).map((t) => ({ value: t.id, label: t.name, hint: t.module_code })),
                            ]}
                            value={form.template_id}
                            onChange={onTemplateChange}
                            placeholder="— None — start blank —"
                            searchPlaceholder="Type to filter templates…"
                            emptyText="No matching template."
                            testId="proposal-template-select"
                            className="mt-1.5"
                        />
                    </div>
                    {/* Iter 66.12 — Module code field removed per operator
                        feedback. The backend auto-derives module_code from
                        the chosen template (or defaults to "Custom"). Kind
                        Attention now spans the full row. Power-users who
                        need to switch modules can do so on the proposal
                        detail page. */}
                    <div>
                        <Label className="text-xs font-semibold">Kind Attention (optional)</Label>
                        <Input value={form.kind_attention} onChange={(e) => setForm({ ...form, kind_attention: e.target.value })} placeholder="Mr. Vikram Shah" className="mt-1.5" data-testid="proposal-kind-attention-input" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Proposal # (optional)</Label>
                        <Input value={form.proposal_number} onChange={(e) => setForm({ ...form, proposal_number: e.target.value })} placeholder="Leave blank for auto" className="mt-1.5 font-mono" data-testid="proposal-number-input" />
                    </div>
                    <CurrencySelect
                        value={form.currency}
                        onChange={(v) => setForm({ ...form, currency: v })}
                        testid="proposal-currency-select"
                    />
                    <div>
                        <Label className="text-xs font-semibold">Remarks (V1)</Label>
                        <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} className="mt-1.5" data-testid="proposal-remarks-input" />
                    </div>
                    <DialogFooter>
                        <FormDialogCancel testId="new-proposal-cancel">Cancel</FormDialogCancel>
                        <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="proposal-submit-btn">
                            {submitting ? "Creating..." : "Create proposal"}
                        </Button>
                    </DialogFooter>
                </form>
        </FormDialog>
    );
}

export default function Proposals() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    // Iter 51.2 — Archive UI is locked to the primary super-admin only.
    const isPrimaryAdmin = !!user?.is_primary_admin;
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reassignTarget, setReassignTarget] = useState(null);
    const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
    const [clientFilter, setClientFilter] = useState("");
    const [textQuery, setTextQuery] = useState("");
    const [mine, setMine] = useState(false);
    const [newOpen, setNewOpen] = useState(false);
    const [newOpenTemplateMode, setNewOpenTemplateMode] = useState(false);
    // PB-002 · Option C — Prefill state populated when arriving at /proposals?new=1&client_id=…
    // from the "Create New Proposal for this Client" affordance on Approved proposals.
    const [newPrefillClientId, setNewPrefillClientId] = useState(null);
    // Iter 51.1 — Archive
    const [showArchived, setShowArchived] = useState(false);
    const archive = useArchiveAction({
        entityType: "Proposal",
        archiveUrl: (row) => `/billing/proposal/${row.id}/archive`,
        unarchiveUrl: (row) => `/billing/proposal/${row.id}/unarchive`,
        onDone: () => load(),
        describe: (row) => (
            <>
                <div><span className="reedos-eyebrow">Proposal</span>{" "}
                    <span className="font-mono text-slate-900">{row.proposal_number}</span></div>
                <div className="mt-1"><span className="reedos-eyebrow">Client</span>{" "}
                    <span className="text-slate-800">{row.client?.name || "—"}</span></div>
                <div className="mt-1"><span className="reedos-eyebrow">Status</span>{" "}
                    <span className="text-slate-800">{row.status}</span></div>
            </>
        ),
    });

    const visibleItems = useMemo(() => {
        const q = textQuery.trim().toLowerCase();
        if (!q) return items;
        return items.filter((p) => {
            const num = (p.proposal_number || "").toLowerCase();
            const cli = (p.client?.name || "").toLowerCase();
            const ka = (p.kind_attention || "").toLowerCase();
            return num.includes(q) || cli.includes(q) || ka.includes(q);
        });
    }, [items, textQuery]);

    const load = async () => {
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (clientFilter) params.client_id = clientFilter;
            params.mine = mine;
            if (showArchived) params.include_archived = true;
            const [pr, cls, tpls] = await Promise.all([
                api.get("/billing/proposal/list", { params }),
                api.get("/billing/client/list"),
                api.get("/billing/proposal-template/list"),
            ]);
            setItems(pr.data.items || []);
            setClients(cls.data.items || []);
            setTemplates(tpls.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load proposals"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, clientFilter, mine, showArchived]);
    useEffect(() => {
        const sp = new URLSearchParams();
        if (statusFilter) sp.set("status", statusFilter);
        setSearchParams(sp, { replace: true });
        // eslint-disable-next-line
    }, [statusFilter]);

    // PB-002 · Option C — Deep-link handler: /proposals?new=1&client_id=<id>
    // Opens the New Proposal dialog with the client pre-selected. Consumed
    // once, then params are stripped so a browser back/forward does not
    // re-trigger the dialog unintentionally.
    useEffect(() => {
        const wantNew = searchParams.get("new") === "1";
        const preClient = searchParams.get("client_id");
        if (wantNew && preClient) {
            setNewPrefillClientId(preClient);
            setNewOpenTemplateMode(false);
            setNewOpen(true);
            const sp = new URLSearchParams(searchParams);
            sp.delete("new");
            sp.delete("client_id");
            setSearchParams(sp, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div data-testid="proposals-page">
            <PageHeader
                eyebrow="Sales · Pre-PO"
                title="Proposals"
                description="Draft commercial proposals from templates, send for approval, then convert directly into a Purchase Order — values copied verbatim."
                actions={
                    <RoleGuard permission="po:write">
                        <div className="flex items-center gap-2" data-testid="proposal-actions">
                            <Button
                                variant="outline"
                                onClick={() => { setNewOpenTemplateMode(true); setNewOpen(true); }}
                                className="gap-1.5"
                                data-testid="new-from-template-btn"
                            >
                                <BookOpen size={14} weight="duotone" /> New from Template
                            </Button>
                            <Button
                                onClick={() => { setNewOpenTemplateMode(false); setNewOpen(true); }}
                                className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                                data-testid="new-proposal-btn"
                            >
                                <Plus size={16} weight="bold" /> New proposal
                            </Button>
                        </div>
                    </RoleGuard>
                }
            />
            <div className="px-6 sm:px-8 py-8">
                <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="proposal-filters">
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            value={textQuery}
                            onChange={(e) => setTextQuery(e.target.value)}
                            placeholder="Search by proposal #, client, or kind attention…"
                            className="pl-8 h-9 text-sm"
                            data-testid="proposals-search-input"
                        />
                    </div>
                    <div className="reedos-eyebrow flex items-center gap-1.5"><Funnel size={12} /> Filter</div>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="proposal-status-filter">
                        <option value="">All status</option>
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <div className="w-56">
                        <SearchableSelect
                            options={[{ value: "", label: "All clients" }, ...clients.map((c) => ({ value: c.id, label: c.name, hint: c.gstin || undefined }))]}
                            value={clientFilter}
                            onChange={setClientFilter}
                            placeholder="All clients"
                            searchPlaceholder="Filter clients…"
                            emptyText="No matching client."
                            testId="proposal-client-filter"
                            className="!h-9"
                        />
                    </div>
                    <OwnershipToggle value={mine} onChange={setMine} testId="proposals-ownership-toggle" />
                    {isPrimaryAdmin && (
                        <ShowArchivedToggle
                            value={showArchived}
                            onChange={setShowArchived}
                            count={items.filter((p) => p.deleted_at).length}
                            testid="proposals-show-archived"
                        />
                    )}
                </div>

                {loading ? (
                    <div className="text-sm text-slate-500">Loading proposals...</div>
                ) : visibleItems.length === 0 ? (
                    <EmptyState
                        icon={<FileDoc size={40} weight="thin" />}
                        title="No proposals yet"
                        description="Start with a template or build from scratch."
                        data-testid="proposals-empty"
                    />
                ) : (
                    <div className="reedos-card overflow-hidden">
                        <table className="w-full text-sm" data-testid="proposals-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">Proposal #</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Client</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Versions</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Status</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Owner</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Created</th>
                                    <th className="px-5 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleItems.map((p) => {
                                    const badge = STATUS_BADGE_MAP[p.status] || { status: p.status };
                                    return (
                                        <tr key={p.id} className={`border-t border-slate-100 reedos-row-hover ${p.deleted_at ? "opacity-60 bg-slate-50/60" : ""}`} data-testid={`proposal-row-${p.id}`}>
                                            <td className="px-5 py-3">
                                                <span className="inline-flex items-center flex-wrap gap-1.5">
                                                    <Link to={`/proposals/${p.id}`} className="font-mono text-xs font-semibold text-slate-900 hover:underline">
                                                        {p.proposal_number}
                                                    </Link>
                                                    <CopyCodeButton
                                                        code={p.proposal_number}
                                                        label="proposal number"
                                                        testid={`proposal-number-copy-${p.id}`}
                                                    />
                                                    {p.deleted_at && <ArchivedBadge testid={`proposal-archived-badge-${p.id}`} />}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3 text-slate-900">{p.client?.name}</td>
                                            <td className="px-5 py-3 text-xs text-slate-700">
                                                {p.versions.length} version{p.versions.length > 1 ? "s" : ""}
                                                {p.approved_version_id && (
                                                    <span className="text-emerald-700 font-semibold ml-1">· approved V{p.versions.find((v) => v.id === p.approved_version_id)?.version_number}</span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3">
                                                <span className="inline-flex items-center gap-1">
                                                    {p.status === "Approved" && <LockKey size={11} weight="fill" className="text-emerald-700" />}
                                                    {p.status === "Converted" && <ArrowsLeftRight size={11} weight="fill" />}
                                                    <StatusBadge {...badge}>{p.status}</StatusBadge>
                                                </span>
                                                {p.converted_to_po_id && (
                                                    <Link to={`/purchase-orders/${p.converted_to_po_id}`} className="block text-[11px] text-slate-500 hover:text-slate-900 mt-0.5">
                                                        → View PO
                                                    </Link>
                                                )}
                                            </td>
                                            <td className="px-5 py-3">
                                                <OwnerCell
                                                    owner={p.owner}
                                                    testId={`proposal-owner-${p.id}`}
                                                    onReassignClick={isSuperAdmin ? () => setReassignTarget(p) : undefined}
                                                />
                                            </td>
                                            <td className="px-5 py-3 text-xs text-slate-500">{formatDate(p.created_date)}</td>
                                            <td className="px-5 py-3 text-right">
                                                <div className="inline-flex items-center gap-2 justify-end">
                                                    {isPrimaryAdmin && (
                                                        p.deleted_at
                                                            ? <UnarchiveRowButton onClick={() => archive.openUnarchive(p)} testid={`proposal-unarchive-${p.id}`} />
                                                            : <ArchiveRowButton onClick={() => archive.openArchive(p)} testid={`proposal-archive-${p.id}`} />
                                                    )}
                                                    <Link to={`/proposals/${p.id}`} data-testid={`proposal-open-${p.id}`} className="text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 text-xs font-medium">
                                                        Open <ArrowRight size={12} weight="bold" />
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <ReassignOwnerDialog
                open={Boolean(reassignTarget)}
                onOpenChange={(o) => { if (!o) setReassignTarget(null); }}
                entityLabel="Proposal"
                entityName={reassignTarget?.proposal_number}
                currentOwner={reassignTarget?.owner}
                reassignUrl={reassignTarget ? `/billing/proposal/${reassignTarget.id}/owner` : ""}
                onSuccess={load}
                testIdPrefix="proposal-reassign"
            />
            <NewProposalDialog
                clients={clients}
                templates={templates}
                open={newOpen}
                onOpenChange={(o) => {
                    setNewOpen(o);
                    // Clear prefill on close so subsequent manual opens start clean.
                    if (!o) setNewPrefillClientId(null);
                }}
                focusTemplate={newOpenTemplateMode}
                prefillClientId={newPrefillClientId}
                onCreated={(p) => navigate(`/proposals/${p.id}`)}
            />
            {/* Iter 51.1 — Archive/Unarchive confirm dialog */}
            {archive.dialog}
        </div>
    );
}
